import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  clearSignupDecision,
  excludeSignupVisitor,
  SignupExperimentEvents,
  signupExperimentProperties,
} from '@/entities/user/signupExperiment';
import { assignTrialPaywallVariant } from '@/entities/user/trialPaywallVariant';

import { API } from '@/shared/api';
import { CONFLICT_STATUS_CODE } from '@/shared/api/errors';
import { CreateUserRequestData } from '@/shared/api/user/types';
import {
  keepGuestPaidTrialClaims,
  readGuestPaidTrialSignupClaim,
  rejectGuestPaidTrialSignupClaim,
  rememberGuestPaidTrialSignupNotice,
} from '@/shared/lib/billing/guestPaidTrialClaim';
import { isTrackingDisabled } from '@/shared/lib/isTrackingDisabled';
import { logout } from '@/shared/lib/logout';
import { isErrorGuard } from '@/shared/lib/types';
import {
  GTMEvents,
  MixpanelEvents,
  setMixpanelUser,
  setMixpanelUserPropertiesOnce,
  setMixpanelUserProperty,
} from '@/shared/thirdParty';

import { LS_IS_JUST_SIGNED_IN_NAME } from '../lib/constants';
import { USER_QUERY_KEYS } from '../lib/userQueryKeys';

type CreateUserParams = {
  isSocial?: boolean;
  onError?: () => void;
};

export const useCreateUser = ({ isSocial = false }: CreateUserParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUserRequestData) => API.createUser(data),
    onError: async (error) => {
      if (isErrorGuard(error, CONFLICT_STATUS_CODE)) {
        const notice = rememberGuestPaidTrialSignupNotice(error);
        const sentClaim = readGuestPaidTrialSignupClaim();
        if (sentClaim && (notice === 'paidTrial.claimAlreadyClaimed' || notice === 'paidTrial.claimNotClaimable')) {
          rejectGuestPaidTrialSignupClaim(sentClaim.session_id);
        }
        const restoreClaims = keepGuestPaidTrialClaims();
        void logout();
        localStorage.clear();
        restoreClaims();
        window.location.reload();
      }
    },
    onSuccess: async (newUser, { guest_checkout }) => {
      setMixpanelUser(newUser.id, newUser.email, newUser.email_type);
      if (guest_checkout) {
        setMixpanelUserProperty(newUser.id, {
          guest_trial_activated_at: new Date().toISOString(),
          guest_trial_buyer: true,
        });
        MixpanelEvents.guestTrialAccountActivated({ guest_trial_session_id: guest_checkout.session_id });
      }
      const freshUser = await API.getUser().catch(() => null);
      if (freshUser) assignTrialPaywallVariant({ user: freshUser });

      if (freshUser) queryClient.setQueryData(USER_QUERY_KEYS.userData, freshUser);
      else await queryClient.refetchQueries({ queryKey: USER_QUERY_KEYS.userData, type: 'active' });

      const excluded = isTrackingDisabled() || /@(?:brask|rask)\.ai$/i.test(newUser.email);
      if (excluded) excludeSignupVisitor();
      setMixpanelUserPropertiesOnce(excluded ? { signup_experiment_excluded: true } : signupExperimentProperties());
      SignupExperimentEvents.signUp({ isSocial });
      clearSignupDecision();
      GTMEvents.setUserId(newUser.id);
      try {
        await GTMEvents.identifyUser(newUser);
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            feature: 'adsTracking',
            phase: 'registrationIdentifyUser',
          },
        });
      }
      GTMEvents.registration(newUser.id);
      // remove this LS item to prevent sending 'login' event after 'registration'
      localStorage.removeItem(LS_IS_JUST_SIGNED_IN_NAME);
    },
  });
};
